import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useCallback, useMemo, useState } from "react";
import { useFindMentorship } from "@/lib/api/mentorships";
import {
  devOverridesEnabled,
  useDevOverridesStore,
} from "@/lib/hooks/admin/use-dev-overrides";
import {
  applyD7TriggerQaToActionSignals,
  getD7QaDismissedActionKinds,
  isForcedD7QaEntityScenario,
} from "../../d7-trigger-qa";
import {
  buildActionSuggestionSignals,
  resolveActionSuggestion,
  type ActionSuggestionDescriptor,
  type ActionSuggestionKind,
} from "./action-suggestion";
import {
  useActionSuggestionDismissals,
  useDismissedKindsByMentorship,
} from "./use-action-suggestion-dismissals";

export interface UseActionSuggestionResult {
  suggestion: ActionSuggestionDescriptor | null;
  isForcedQaEntitySuggestion: boolean;
  dismiss: () => void;
}

export function useActionSuggestion(
  menteeId: string | null,
): UseActionSuggestionResult {
  const store = useSendbirdStateContext();
  const mentorId = store.config.userId;
  const { data: mentorship } = useFindMentorship(mentorId, menteeId ?? "");
  const rawQaScenario = useDevOverridesStore(
    (state) => state.d7TriggerQaScenario,
  );
  const qaScenario = devOverridesEnabled ? rawQaScenario : null;
  const mentorshipId = mentorship?.id ?? null;

  const dismissedByMentorship = useDismissedKindsByMentorship();
  const dismissKind = useActionSuggestionDismissals((state) => state.dismiss);
  const dismissedKinds = useMemo(
    () => new Set(mentorshipId ? dismissedByMentorship[mentorshipId] : []),
    [dismissedByMentorship, mentorshipId],
  );

  const [loadedMentorshipId, setLoadedMentorshipId] = useState(mentorshipId);
  const [loadedQaScenario, setLoadedQaScenario] = useState(qaScenario);
  const [dismissedQaKinds, setDismissedQaKinds] = useState<
    ReadonlySet<ActionSuggestionKind>
  >(() => new Set());

  if (mentorshipId !== loadedMentorshipId) {
    setLoadedMentorshipId(mentorshipId);
    setDismissedQaKinds(new Set());
  }

  if (qaScenario !== loadedQaScenario) {
    setLoadedQaScenario(qaScenario);
    setDismissedQaKinds(new Set());
  }

  const effectiveDismissedKinds = getD7QaDismissedActionKinds(
    qaScenario,
    dismissedKinds,
    dismissedQaKinds,
  );
  const suggestion = mentorship
    ? resolveActionSuggestion(
        applyD7TriggerQaToActionSignals(
          buildActionSuggestionSignals(mentorship),
          qaScenario,
        ),
        effectiveDismissedKinds,
      )
    : null;

  const dismiss = useCallback(() => {
    if (!mentorshipId || !suggestion?.dismissible) return;
    if (qaScenario) {
      setDismissedQaKinds((current) => new Set(current).add(suggestion.kind));
      return;
    }
    dismissKind(mentorshipId, suggestion.kind);
  }, [dismissKind, mentorshipId, qaScenario, suggestion]);

  return {
    suggestion,
    isForcedQaEntitySuggestion: isForcedD7QaEntityScenario(qaScenario),
    dismiss,
  };
}
